import {lazy, Suspense} from 'react';
import {Navigate, useLocation, useRoutes} from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
import PlatformOwnerGuard from '../guards/PlatformOwnerGuard';
// config
import {PATH_AFTER_LOGIN} from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const {pathname} = useLocation();

    return (
        <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')}/>}>
            <Component {...props} />
        </Suspense>
    );
};

export default function Router() {
    return useRoutes([
        {
            path: 'auth',
            children: [
                {
                    path: 'login',
                    element: (
                        <GuestGuard>
                            <Login/>
                        </GuestGuard>
                    ),
                },
                {
                    path: 'register',
                    element: (
                        <GuestGuard>
                            <Register/>
                        </GuestGuard>
                    ),
                },
                {path: 'login-unprotected', element: <Login/>},
                {path: 'register-unprotected', element: <Register/>},
                {path: 'reset-password', element: <ResetPassword/>},
                {path: 'set-password', element: <SetPassword/>},
                {path: 'verify', element: <VerifyCode/>},
            ],
        },

        {
            path: 'onboarding',
            element: (
                <AuthGuard>
                    <Onboarding/>
                </AuthGuard>
            ),
        },
        {
            path: 'onboarding/setup',
            element: (
                <AuthGuard>
                    <PostPaymentOnboarding/>
                </AuthGuard>
            ),
        },
        {
            path: 'onboarding/setup/:step',
            element: (
                <AuthGuard>
                    <PostPaymentOnboarding/>
                </AuthGuard>
            ),
        },

        // Dashboard Routes
        {
            path: 'dashboard',
            element: (
                <AuthGuard>
                    <DashboardLayout/>
                </AuthGuard>
            ),
            children: [
                {element: <Navigate to={PATH_AFTER_LOGIN} replace/>, index: true},
                {path: 'app', element: <GeneralApp/>},
                {path: 'sales-agent', element: <SalesAgent/>},
                {path: 'comprar-creditos', element: <BuyCredits/>},
                {path: 'referrals', element: <Referrals/>},
                {path: 'ecommerce', element: <GeneralEcommerce/>},
                {path: 'analytics', element: <PlatformOwnerGuard><GeneralAnalytics/></PlatformOwnerGuard>},
                {path: 'banking', element: <GeneralBanking/>},
                {path: 'booking', element: <GeneralBooking/>},

                {
                    path: 'e-commerce',
                    children: [
                        {element: <Navigate to="/dashboard/e-commerce/shop" replace/>, index: true},
                        {path: 'shop', element: <EcommerceShop/>},
                        {path: 'product/:name', element: <EcommerceProductDetails/>},
                        {path: 'list', element: <EcommerceProductList/>},
                        {path: 'product/new', element: <EcommerceProductCreate/>},
                        {path: 'product/:name/edit', element: <EcommerceProductCreate/>},
                        {path: 'checkout', element: <EcommerceCheckout/>},
                        {path: 'invoice', element: <EcommerceInvoice/>},
                    ],
                },
                {
                    path: 'teams',
                    children: [
                        {element: <Navigate to="/dashboard/teams/list" replace/>, index: true},
                        {path: 'cards', element: <UserCards/>},
                        {
                            path: 'list', element: (
                                <RoleBasedGuard>
                                    <TeamsList/>
                                </RoleBasedGuard>
                            )
                        },
                        {path: 'new', element: <TeamsCreate/>},
                        {path: ':id/edit', element: <TeamsCreate/>},
                    ],
                },
                {
                    path: 'campaigns',
                    children: [
                        {element: <Navigate to="/dashboard/campaigns/list" replace/>, index: true},
                        {
                            path: 'list', element: (
                                <RoleBasedGuard>
                                    <CampaignsList/>
                                </RoleBasedGuard>
                            )
                        },
                        {path: 'new', element: <CampaignNew/>},
                        {path: 'guided-search', element: <GuidedLeadSearch/>},
                        {path: 'guided-new', element: <LeadSearchWizard/>},
                        {path: 'guided-legacy', element: <GuidedCampaignSearch/>},
                        {path: ':id/edit', element: <CampaignEdit/>},
                    ],
                },
                {
                    path: 'contacts',
                    children: [
                        {element: <Navigate to="/dashboard/contacts/list" replace/>, index: true},
                        {path: 'cards', element: <UserCards/>},
                        {path: 'list', element: <ContactsList/>},
                        {path: 'new', element: <Navigate to="/dashboard/contacts/list" replace/>},
                        {path: ':id/edit', element: <ContactCreate/>},
                    ],
                },
                {
                    path: 'config',
                    children: [
                        {element: <Navigate to="/dashboard/user/account" replace/>, index: true},
                        {path: 'kanban', element: <KanbanConfig/>},
                    ]
                },
                {
                    path: 'user',
                    children: [
                        {element: <Navigate to="/dashboard/user/account" replace/>, index: true},
                        {path: 'profile', element: <UserProfile/>},
                        {path: 'cards', element: <UserCards/>},
                        {path: 'list', element: <UserList/>},
                        {path: 'new', element: <UserCreate/>},
                        {path: 'icps', element: <CustomerProfiles/>},
                        {path: ':name/edit', element: <UserCreate/>},
                        {path: 'account', element: <UserAccount/>},
                    ],
                },
                {
                    path: 'blog',
                    children: [
                        {element: <Navigate to="/dashboard/blog/posts" replace/>, index: true},
                        {path: 'posts', element: <BlogPosts/>},
                        {path: 'post/:title', element: <BlogPost/>},
                        {path: 'new-post', element: <BlogNewPost/>},
                    ],
                },
                {
                    path: 'mail',
                    children: [
                        {element: <Navigate to="/dashboard/mail/all" replace/>, index: true},
                        {path: 'label/:customLabel', element: <Mail/>},
                        {path: 'label/:customLabel/:mailId', element: <Mail/>},
                        {path: ':systemLabel', element: <Mail/>},
                        {path: ':systemLabel/:mailId', element: <Mail/>},
                    ],
                },
                {
                    path: 'chat',
                    children: [
                        {element: <Chat/>, index: true},
                        {path: 'new', element: <Chat/>},
                        {path: ':conversationKey', element: <Chat/>},
                    ],
                },
                {path: 'calendar', element: <Calendar/>},
                {path: 'whatsapp', element: <Whatsapp/>},
                {
                    path: 'leads',
                    children: [
                        {element: <LeadsKanban/>, index: true},
                        {path: 'list', element: <CompaniesList/>},
                        {path: 'board', element: <LeadsKanban/>},
                        {path: ':id', element: <LeadsCreate/>},
                        {path: ':id/edit', element: <LeadsCreate/>},
                    ]
                },
                {path: 'cnpj-assistant', element: <CnpjAssistantChat/>},
                {path: 'pesquisas/nova', element: <LeadSearchWizard/>},
                {
                    path: 'outreach-campaigns',
                    children: [
                        {element: <Navigate to="/dashboard/outreach-campaigns/list" replace/>, index: true},
                        {path: 'list', element: <OutreachCampaignsList/>},
                        {path: 'builder', element: <Navigate to="/dashboard/campaigns/list" replace/>},
                        {path: ':id', element: <OutreachCampaignDetails/>},
                    ],
                },
            ],
        },

        // Standalone landing pages - own header/footer, no MainLayout
        {path: '/', element: <LandingPage3/>},
        {path: '/landingpage3', element: <LandingPage3/>},
        {path: '/para/:slug', element: <SectorLanding/>},
        {path: '/landinpage4', element: <LandingPage4/>},
        {path: '/landingpage5', element: <LandingPage5/>},
        {path: '/landingpage6', element: <LandingPage6/>},
        {path: '/landingpage10', element: <LandingPage10/>},
        {path: '/landingpage11', element: <LandingPage11/>},
        {path: '/blog', element: <PublicBlogIndex/>},
        {path: '/blog/:slug', element: <PublicBlogPost/>},
        {path: '/politica-de-privacidade', element: <LegalPage kind="privacy"/>},
        {path: '/termos-de-uso', element: <LegalPage kind="terms"/>},
        {path: '/privacy', element: <Navigate to="/politica-de-privacidade" replace/>},
        {path: '/privacy-policy', element: <Navigate to="/politica-de-privacidade" replace/>},
        {path: '/terms', element: <Navigate to="/termos-de-uso" replace/>},
        {path: '/terms-of-service', element: <Navigate to="/termos-de-uso" replace/>},

        // Main Routes – path '/' must come before path '*' so / shows home, not 404
        {
            path: '/',
            element: <MainLayout/>,
            children: [
                {path: 'home', element: <HomePage/>},
                {path: 'landingpage', element: <LandingPage/>},
                {path: 'pacotes', element: <PacotesPage/>},
                {path: 'hotmart', element: <HomePage/>},
                {path: 'about-us', element: <About/>},
                {path: 'contact-us', element: <Contact/>},
                {path: 'contato', element: <Contact/>},
                {path: 'faqs', element: <Faqs/>},
                {
                    path: 'empresas',
                    children: [
                        {element: <Companies/>, index: true},
                        {path: 'busca', element: <CompaniesSearch/>},
                        {path: ':cnpj', element: <CompanyDetails/>},
                    ]
                },
            ],
        },
        {
            path: '*',
            element: <LogoOnlyLayout/>,
            children: [
                {path: 'coming-soon', element: <ComingSoon/>},
                {path: 'maintenance', element: <Maintenance/>},
                {path: 'pricing', element: <Pricing/>},
                {path: 'payment', element: <Payment/>},
                {path: '500', element: <Page500/>},
                {path: '404', element: <NotFound/>},
                {path: '*', element: <Navigate to="/404" replace/>},
            ],
        },
        {path: '*', element: <Navigate to="/404" replace/>},
    ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const SetPassword = Loadable(lazy(() => import('../pages/auth/SetPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));
const Onboarding = Loadable(lazy(() => import('../pages/Onboarding')));
const PostPaymentOnboarding = Loadable(lazy(() => import('../pages/PostPaymentOnboarding')));
// Dashboard
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
const GeneralEcommerce = Loadable(lazy(() => import('../pages/dashboard/GeneralEcommerce')));
const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));
const GeneralBanking = Loadable(lazy(() => import('../pages/dashboard/GeneralBanking')));
const GeneralBooking = Loadable(lazy(() => import('../pages/dashboard/GeneralBooking')));
const EcommerceShop = Loadable(lazy(() => import('../pages/dashboard/EcommerceShop')));
const EcommerceProductDetails = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductDetails')));
const EcommerceProductList = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductList')));
const EcommerceProductCreate = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductCreate')));
const EcommerceCheckout = Loadable(lazy(() => import('../pages/dashboard/EcommerceCheckout')));
const EcommerceInvoice = Loadable(lazy(() => import('../pages/dashboard/EcommerceInvoice')));
const BlogPosts = Loadable(lazy(() => import('../pages/dashboard/BlogPosts')));
const BlogPost = Loadable(lazy(() => import('../pages/dashboard/BlogPost')));
const BlogNewPost = Loadable(lazy(() => import('../pages/dashboard/BlogNewPost')));
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const Referrals = Loadable(lazy(() => import('../pages/dashboard/Referrals')));
const BuyCredits = Loadable(lazy(() => import('../pages/dashboard/BuyCredits')));
const CustomerProfiles = Loadable(lazy(() => import('../pages/dashboard/CustomerProfiles')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));
const TeamsCreate = Loadable(lazy(() => import('../pages/dashboard/TeamsCreate')));
const LeadsCreate = Loadable(lazy(() => import('../pages/dashboard/LeadsCreate')));
const CampaignEdit = Loadable(lazy(() => import('../pages/dashboard/CampaignEdit')));
const ContactCreate = Loadable(lazy(() => import('../pages/dashboard/ContactCreate')));
const TeamsList = Loadable(lazy(() => import('../pages/dashboard/TeamsList')));
const ContactsList = Loadable(lazy(() => import('../pages/dashboard/ContactList')));
const CompaniesList = Loadable(lazy(() => import('../pages/dashboard/CompaniesList')));
const CampaignsList = Loadable(lazy(() => import('../pages/dashboard/CampaignsList')));
const CampaignNew = Loadable(lazy(() => import('../pages/dashboard/CampaignNewSearch')));
const GuidedLeadSearch = Loadable(lazy(() => import('../pages/dashboard/GuidedLeadSearch')));
const GuidedCampaignSearch = Loadable(lazy(() => import('../pages/dashboard/GuidedCampaignSearch')));
const LeadSearchWizard = Loadable(lazy(() => import('../pages/dashboard/LeadSearchWizard')));
const Chat = Loadable(lazy(() => import('../pages/dashboard/Chat')));
const Mail = Loadable(lazy(() => import('../pages/dashboard/Mail')));
const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')));
const Whatsapp = Loadable(lazy(() => import('../pages/dashboard/Whatsapp')));
const LeadsKanban = Loadable(lazy(() => import('../pages/dashboard/LeadsKanban')));
const KanbanConfig = Loadable(lazy(() => import('../pages/dashboard/KanbanConfig')));
const CnpjAssistantChat = Loadable(lazy(() => import('../pages/dashboard/CnpjAssistantChat')));
const SalesAgent = Loadable(lazy(() => import('../pages/dashboard/SalesAgent')));
const OutreachCampaignsList = Loadable(lazy(() => import('../pages/dashboard/OutreachCampaignsList')));
const OutreachCampaignDetails = Loadable(lazy(() => import('../pages/dashboard/OutreachCampaignDetails')));
// Main
const HomePage = Loadable(lazy(() => import('../pages/Home')));
const LandingPage = Loadable(lazy(() => import('../pages/LandingPage')));
const LandingPage3 = Loadable(lazy(() => import('../pages/LandingPage3')));
const LandingPage4 = Loadable(lazy(() => import('../pages/LandingPage4')));
const LandingPage5 = Loadable(lazy(() => import('../pages/LandingPage5')));
const LandingPage6 = Loadable(lazy(() => import('../pages/LandingPage6')));
const LandingPage10 = Loadable(lazy(() => import('../pages/LandingPage10')));
const LandingPage11 = Loadable(lazy(() => import('../pages/LandingPage11')));
const LegalPage = Loadable(lazy(() => import('../pages/LegalPage')));
const PublicBlogIndex = Loadable(lazy(() => import('../pages/blog/BlogIndex')));
const PublicBlogPost = Loadable(lazy(() => import('../pages/blog/BlogPost')));
const SectorLanding = Loadable(lazy(() => import('../pages/SectorLanding')));
const PacotesPage = Loadable(lazy(() => import('../pages/Pacotes')));
const About = Loadable(lazy(() => import('../pages/About')));
const Contact = Loadable(lazy(() => import('../pages/Contact')));
const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
const Companies = Loadable(lazy(() => import('../pages/Companies')));
const CompaniesSearch = Loadable(lazy(() => import('../pages/CompaniesSearch')));
const CompanyDetails = Loadable(lazy(() => import('../pages/CompanyDetails')));
